import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FiActivity, FiCheck } from "react-icons/fi"; 
import AppShell from "../components/AppShell";
import { maybeSendFeedbackEmail } from "../lib/feedbackemail";
import {
  ProgressBar,
  WeekTabs,
  CheckinCard,
  ActivityCard,
  LearnSection,
} from "../components/recovery";

import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { useLessons } from "../hooks/useLessons";

import WeekUnlockPopup from "../components/recovery/WeekUnlockPopup";
import { useWeekContext } from "../context/WeekContext";
import { useRecoveryProgram } from "../hooks/useRecoveryProgram";
import { useWeekQuestionnaireButton } from "../hooks/useWeekQuestionnaireButton";

import MoodIcon from "../assets/Smiley-Smile-1--Streamline-Ultimate 1.svg";
import SymptomsIcon from "../assets/Checkup-Chart--Streamline-Ultimate 1.svg";
import BleedingIcon from "../assets/Blood-Types-1--Streamline-Ultimate 1.svg";

// ============================================================================
// ── GP READINESS CARD COMPONENT (INTERNAL) ──────────────────────────────────
// ============================================================================
interface GPReadinessCardProps {
  moodDays: number;
  symptomDays: number;
  bleedingDays: number;
  isSubmitted: boolean;
}

const GPReadinessCard: React.FC<GPReadinessCardProps> = ({
  moodDays,
  symptomDays,
  bleedingDays,
  isSubmitted,
}) => {
  return (
    <div className="mx-6 mt-6 p-5 bg-white rounded-[24px] border border-gray-100 shadow-[0_6px_20px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 flex items-center justify-center rounded-lg bg-[#F3F6F5]">
            <FiActivity className="text-[#1E5E5B]" size={16} />
          </div>
          <span className="text-[14px] font-semibold text-[#0C2624]">
            GP Report Readiness
          </span>
        </div>
      </div>

      <div className="flex gap-2.5">
        <Tile label="Mood" value={moodDays} />
        <Tile label="Symptoms" value={symptomDays} />
        <Tile label="Bleeding" value={bleedingDays} />
      </div>

      <div className="mt-4 pt-3 border-t border-gray-50">
        <div
          className={`flex items-center justify-between px-3 py-2 rounded-xl border ${
            isSubmitted
              ? "bg-[#F3F9F7] border-[#D7E8E2]"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-2.5 h-2.5 rounded-full ${
                isSubmitted ? "bg-[#1E5E5B]" : "bg-gray-300"
              }`}
            />
            <span className="text-[12px] font-medium text-[#0C2624]">
              Weekly report status
            </span>
          </div>

          {isSubmitted ? (
            <div className="flex items-center gap-1 text-[#1E5E5B] text-[12px] font-semibold">
              <FiCheck size={14} /> Completed
            </div>
          ) : (
            <span className="text-[12px] text-gray-500 font-medium">
              In progress
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Tile = ({ label, value }: { label: string; value: number }) => {
  const getColor = () => {
    if (value < 3) return "#FFCC80";
    if (value === 3) return "#C5E1A5";
    if (value < 7) return "#81C784";
    return "#1E5E5B";
  };
  const color = getColor();

  return (
    <div className="flex-1 bg-[#F3F4F6] rounded-xl p-3 border border-gray-200 flex flex-col items-center sm:items-start">
      <div className="text-[10px] text-gray-500 mb-2 uppercase tracking-wide font-bold">
        {label}
      </div>
      <div className="bg-white border border-gray-200 mb-2 shadow-sm transition-all w-full grid grid-cols-4 gap-y-1.5 gap-x-1 rounded-[12px] px-2 py-2 justify-items-center sm:flex sm:flex-nowrap sm:rounded-full sm:px-2 sm:py-1 sm:gap-0 sm:justify-between">
        {[...Array(7)].map((_, i) => (
          <div
            key={i}
            className="transition-all duration-300 rounded-full w-2.5 h-2.5 sm:w-4 sm:h-4"
            style={{
              backgroundColor: i < value ? color : "#D1D5DB",
              transform: i < value ? "scale(1.08)" : "scale(1)",
              boxShadow: i < value ? "0 2px 6px rgba(0,0,0,0.12)" : "none",
            }}
          />
        ))}
      </div>
      <div className="text-[12px] font-semibold text-[#0C2624]">
        {value}/7 days
      </div>
    </div>
  );
};

// ============================================================================
// ── Questionnaire Button ──────────────────────────────────────────────────
// ============================================================================
const WeekQuestionnaireButton: React.FC<{
  weekNumber: number;
  onPress: () => void;
}> = ({ weekNumber, onPress }) => {
  const { showButton, isSubmitted, loading } = useWeekQuestionnaireButton(weekNumber);

  if (loading || !showButton || weekNumber === 6) return null;

  return (
    <div className="px-6 mt-6">
      <button
        type="button"
        onClick={onPress}
        className="w-full flex items-center gap-4 p-5 rounded-[24px] transition-all duration-200 border-2"
        style={{
          background: isSubmitted ? "#fcfdfc" : "#ffffff",
          borderColor: isSubmitted ? "#e8f0e8" : "#1e5e5b",
          cursor: "pointer",
          boxShadow: isSubmitted ? "none" : "0 4px 12px rgba(30,94,91,0.08)",
        }}
      >
        <div 
          className="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-[16px]"
          style={{ background: isSubmitted ? "#d1e0b7" : "#1e5e5b" }}
        >
          {isSubmitted ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e5e5b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
               <path d="M9 11l3 3L22 4" />
               <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
            </svg>
          )}
        </div>

        <div className="flex-1 text-left">
          <div className="flex items-center gap-2 mb-1">
            <span 
              className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ 
                background: isSubmitted ? "#e8f0e8" : "#d1e0b7",
                color: "#1e5e5b" 
              }}
            >
              {isSubmitted ? "Completed" : "Action Required"}
            </span>
          </div>
          <h3 className="text-lg font-bold text-[#0c2624] leading-tight font-dm-sans">
            {isSubmitted 
              ? `Review Week ${weekNumber} Check-in` 
              : `Start Week ${weekNumber} Check-in`}
          </h3>
          <p className="text-sm text-gray-500 mt-1 font-dm-sans leading-snug">
            {isSubmitted 
              ? "See the responses you've submitted for this week." 
              : "Check in on your symptoms and physical recovery (2 mins)"}
          </p>
        </div>

        <div className="flex items-center justify-center">
          {isSubmitted ? (
            <span className="text-xs font-bold text-[#1e5e5b] underline underline-offset-4 decoration-2 opacity-80">
              Review
            </span>
          ) : (
            <div className="w-8 h-8 rounded-full bg-[#fcf9f7] flex items-center justify-center shadow-inner">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1e5e5b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </div>
          )}
        </div>
      </button>
    </div>
  );
};

// ============================================================================
// ── Main Recovery Page ───────────────────────────────────────────────────────
// ============================================================================
const Recovery: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const learnSectionRef = useRef<HTMLDivElement>(null);

  const { weekProgram, loading: loadingProgram } = useRecoveryProgram();
  const { weeks, progress, loading: loadingLessons } = useLessons();

  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [activitiesCompleted, setActivitiesCompleted] = useState<Record<string, boolean>>({});
  const [moodDays, setMoodDays] = useState(0);
  const [symptomDays, setSymptomDays] = useState(0);
  const [bleedingDays, setBleedingDays] = useState(0);

  const {
    weekStates,
    unlockedWeek,
    pendingUnlockWeek,
    weekCompletions,
    startDateId,
    showPopupForWeek,
    triggerPopup,
    dismissPopup,
    confirmUnlock,
  } = useWeekContext();

  // ── Sync selectedWeek to unlockedWeek on first load ──────────────────────
  const hasInitialisedWeek = useRef(false);

  useEffect(() => {
    if (!hasInitialisedWeek.current && unlockedWeek > 1) {
      hasInitialisedWeek.current = true;
      setSelectedWeek(unlockedWeek);
    }
  }, [unlockedWeek]);

  const isReadOnly = selectedWeek < unlockedWeek;
  const { isSubmitted: isQuestionnaireDone } = useWeekQuestionnaireButton(selectedWeek);

  const handleWeekSelect = (week: number) => {
    if (week === pendingUnlockWeek) {
      triggerPopup();
    } else if (week <= unlockedWeek) {
      setSelectedWeek(week);
    }
  };

  // FIX 4a: Intercept incoming scroll state navigation commands from dashboard links
  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo === "learning" && !loadingLessons && !loadingProgram) {
      const timer = setTimeout(() => {
        learnSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [location.state, loadingLessons, loadingProgram]);

  // SNAPSHOT FOR GP READINESS
  useEffect(() => {
    if (!user) return;

    const logsRef = collection(db, "users", user.uid, "recoveryDailyLogs");
    const q = query(logsRef, where("weekNumber", "==", selectedWeek));

    const unsubscribe = onSnapshot(q, (snap) => {
      const moodDates = new Set();
      const symptomDates = new Set();
      const bleedingDates = new Set();

      snap.docs.forEach((doc) => {
        const data = doc.data();
        const dId = data.dateId;
        if (data.mood?.key) moodDates.add(dId);
        if (data.symptoms) {
          const hasGeneral = Object.keys(data.symptoms).some(
            (k) => k !== "vaginalBleeding" && data.symptoms[k]?.level
          );
          if (hasGeneral) symptomDates.add(dId);
          if (data.symptoms.vaginalBleeding?.flow) bleedingDates.add(dId);
        }
      });

      setMoodDays(moodDates.size);
      setSymptomDays(symptomDates.size);
      setBleedingDays(bleedingDates.size);
    });

    return () => unsubscribe();
  }, [user, selectedWeek]);

  // LOAD ACTIVITIES
  useEffect(() => {
    const loadActivities = async () => {
      if (!user) return;
      const ref = doc(db, "users", user.uid, "recoveryWeeklyActivities", `physical_wellbeing_6w_${selectedWeek}`);
      const snap = await getDoc(ref);
      setActivitiesCompleted(snap.exists() ? snap.data()?.activities ?? {} : {});
    };
    loadActivities();
  }, [user, selectedWeek]);

  const toggleActivity = useCallback(async (activityId: string) => {
    if (!user || isReadOnly) return;
    setActivitiesCompleted((prev) => {
      const updated = { ...prev, [activityId]: !prev[activityId] };
      const ref = doc(db, "users", user.uid, "recoveryWeeklyActivities", `physical_wellbeing_6w_${selectedWeek}`);
      setDoc(ref, { 
        userId: user.uid, 
        programmeId: "physical_wellbeing_6w", 
        weekNumber: selectedWeek, 
        activities: updated, 
        updatedAt: serverTimestamp() 
      }, { merge: true });
      return updated;
    });
  }, [user, isReadOnly, selectedWeek]);

  const weekData = weekProgram[selectedWeek];
  const meta = weekData?.meta ?? { theme: "", intro: "" };
  const currentActivities = weekData?.activities ?? [];
  const lessonsForWeek = useMemo(() => weeks[selectedWeek] ?? [], [weeks, selectedWeek]);
  const activitiesCompletedCount = currentActivities.filter((a) => activitiesCompleted[a.id]).length;
  const activitiesTotal = currentActivities.length;

  // ── HIGH QUALITY BRANDED SPINNER (Initial Load) ───────────────────────────
  if (loadingProgram || loadingLessons) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100%',
        minHeight: '-webkit-fill-available', 
        backgroundColor: '#fcf9f7',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2147483647,
        pointerEvents: 'all'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '20px',
          boxSizing: 'border-box'
        }} />

        <div style={{
          fontFamily: 'DM Sans, sans-serif',
          textAlign: 'center'
        }}>
          <p style={{
            color: '#0c2624',
            fontSize: '18px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          body { overflow: hidden !important; }
        `}</style>
      </div>
    );
  }

  return (
    <AppShell
      headerConfig={{
        title: "Your Recovery",
        gradient: "linear-gradient(to bottom right, #1E5E5B, #C6D8A5)",
        height: 224,
        headerExtra: (
          <div className="mt-[13px]">
            <p className="m-0 mb-2 font-dm-sans text-base font-medium text-gray-100">This week's focus</p>
            <div className="flex h-11 items-center justify-center rounded-full bg-content-bg px-6">
              <span className="font-dm-sans text-base font-bold text-brand-primary">Physical Wellbeing</span>
            </div>
          </div>
        ),
      }}
    >
      <div className="relative z-10 bg-content-bg rounded-t-[32px] pt-6 pb-[72px]">
        <div className="px-6">
          <ProgressBar value={selectedWeek} max={6} label={`Week ${selectedWeek} / 6`} />
        </div>
        
        <WeekTabs
          selectedWeek={selectedWeek}
          onSelectWeek={handleWeekSelect}
          weekStates={weekStates}
        />

        {isReadOnly ? (
          <div className="mx-6 mt-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2">
            <FiActivity className="text-blue-500" size={18} />
            <span className="text-sm text-blue-700 font-medium">Week {selectedWeek} is complete — view only</span>
          </div>
        ) : selectedWeek === unlockedWeek && selectedWeek < 6 ? (() => {
          const metrics = weekCompletions?.[selectedWeek] || { videoTotal: 0, videoWatched: 0, pct: 0 };
          const targetedCount = Math.ceil(metrics.videoTotal * 0.8);
          const videosLeft = Math.max(0, targetedCount - metrics.videoWatched);
          const hasMetVideoTarget = metrics.pct >= 80;

          let daysLeft = 0;
          if (startDateId) {
            const parts = startDateId.split("-");
            if (parts.length === 3) {
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              const start = new Date(year, month, day);

              const nextWeekStart = new Date(start.getTime());
              nextWeekStart.setDate(nextWeekStart.getDate() + selectedWeek * 7);
              nextWeekStart.setHours(0, 0, 0, 0);

              const today = new Date();
              today.setHours(0, 0, 0, 0);

              const diffTime = nextWeekStart.getTime() - today.getTime();
              daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
          }
          const hasMetTimeTarget = daysLeft <= 0;

          let bannerStyle = "bg-amber-50 border-amber-200 text-amber-700";
          let bannerMessage = "";

          if (!hasMetVideoTarget && !hasMetTimeTarget) {
            bannerMessage = `To unlock Week ${selectedWeek + 1}: Watch ${videosLeft} more video${videosLeft > 1 ? 's' : ''} AND wait ${daysLeft} more day${daysLeft > 1 ? 's' : ''}.`;
          } else if (hasMetVideoTarget && !hasMetTimeTarget) {
            bannerMessage = `Video target met (${metrics.videoWatched}/${metrics.videoTotal} watched)! Next week will unlock automatically in ${daysLeft} day${daysLeft > 1 ? 's' : ''}.`;
          } else if (!hasMetVideoTarget && hasMetTimeTarget) {
            bannerMessage = `Time milestone reached! Finish ${videosLeft} more video${videosLeft > 1 ? 's' : ''} to unlock Week ${selectedWeek + 1} instantly.`;
          } else {
            bannerStyle = "bg-[#F3F9F7] border-[#D7E8E2] text-[#1E5E5B]";
            bannerMessage = `All requirements met! Click the "Week ${selectedWeek + 1}" tab above to unlock your next module.`;
          }

          return (
            <div className={`mx-6 mt-4 px-4 py-3 border rounded-xl flex items-center gap-2 transition-all duration-300 ${bannerStyle}`}>
               <span className="text-sm font-medium">
                 {bannerMessage}
               </span>
            </div>
          );
        })() : null}

        <GPReadinessCard moodDays={moodDays} symptomDays={symptomDays} bleedingDays={bleedingDays} isSubmitted={isQuestionnaireDone} />

        <div className="px-6 mt-6">
          <h2 className="m-0 mb-4 text-2xl font-semibold text-brand-dark font-dm-sans">Today's Check-in</h2>
          <div className="flex flex-col gap-3 ">
            <CheckinCard icon={MoodIcon} color="#D1E0B7" title="Log your mood" isReadOnly={isReadOnly} onClick={() => navigate("/recovery/checkins/mood", { state: { week: selectedWeek, backgroundLocation: location } })} />
            <CheckinCard icon={SymptomsIcon} color="#EDE4F1" title="Log your symptoms" isReadOnly={isReadOnly} onClick={() => navigate("/recovery/checkins/symptoms", { state: { week: selectedWeek, backgroundLocation: location } })} />
            <CheckinCard icon={BleedingIcon} color="#F5ECE8" title="Track your vaginal bleeding" isReadOnly={isReadOnly} onClick={() => navigate("/recovery/checkins/bleeding", { state: { week: selectedWeek, backgroundLocation: location } })} />
          </div>
        </div>

        <div className="px-6 mt-7">
          <div className="flex items-center justify-between mb-4">
            <h2 className="m-0 text-2xl font-semibold text-brand-dark font-dm-sans">Activities for the week</h2>
            <span className="text-sm text-gray-500">{activitiesCompletedCount}/{activitiesTotal}</span>
          </div>
          <ProgressBar value={activitiesCompletedCount} max={activitiesTotal} size="sm" className="mb-6" />
          <div className="flex flex-col gap-4">
            {currentActivities.map((a) => (
              <ActivityCard
                key={a.id}
                title={a.title}
                description={a.description}
                stripeColor={a.stripeColor}
                isCompleted={activitiesCompleted[a.id] ?? false}
                onToggle={() => toggleActivity(a.id)}
                isReadOnly={isReadOnly}
              />
            ))}
          </div>
        </div>

        {selectedWeek <= unlockedWeek && <WeekQuestionnaireButton weekNumber={selectedWeek} onPress={() => navigate(`/questionnaire/week_${selectedWeek}`)} />}
        
        <div ref={learnSectionRef}>
          <LearnSection
            weekNumber={selectedWeek}
            weekIntro={meta.intro}
            lessons={lessonsForWeek}
            progress={progress}
            isLoading={loadingLessons || loadingProgram}
            onLessonClick={(id) => navigate(`/recovery/week/${selectedWeek}/lesson/${id}`, { state: { week: selectedWeek } })}
          />
        </div>
      </div>

      {showPopupForWeek !== null && (
        <WeekUnlockPopup
          weekNumber={showPopupForWeek}
          onDismiss={dismissPopup}
          onGoToWeek={async (w) => {
            if (user?.uid) {
              maybeSendFeedbackEmail(user.uid, w);
            }
            await confirmUnlock();
            setSelectedWeek(w);
          }}
        />
      )}
    </AppShell>
  );
};

export default Recovery;